/**
 * MobilePlayerMinimized Component
 *
 * Apple Music liquid glass inspired minimized player bar.
 * Floating pill shape with frosted glass morphism, positioned above the bottom nav.
 *
 * Features:
 * - Liquid glass design with backdrop blur and semi-transparent background
 * - Floating pill shape with rounded corners
 * - Compact artwork thumbnail with rounded corners
 * - Current chapter/title and subtitle
 * - Play/pause toggle (square icon for pause, play triangle for play)
 * - 30-second rewind button
 * - Subtle progress indicator at the top of the pill
 * - Tap to expand to fullscreen, swipe up gesture
 */

import { Play, Pause, RotateCcw } from "lucide-react";
import {
	usePlayerStore,
	playerSelectors,
	getPlayerActions,
} from "@/lib/player/player.store";
import { usePlayerGestures } from "./hooks/usePlayerGestures.hook";

interface MobilePlayerMinimizedProps {
	onExpand: () => void;
}

export function MobilePlayerMinimized({
	onExpand,
}: MobilePlayerMinimizedProps) {
	const isPaused = usePlayerStore((state) => state.isPaused);
	const chapters = usePlayerStore((state) => state.chapters);
	const activeChapterIndex = usePlayerStore(
		(state) => state.activeChapterIndex,
	);
	const currentMedia = usePlayerStore((state) => state.currentMedia);
	const currentChapter = playerSelectors.getCurrentChapter(
		usePlayerStore.getState(),
	);

	// Use shared selector for progress calculation so it's consistent across all views.
	const progress = playerSelectors.getProgressPercentage(
		usePlayerStore.getState(),
	);

	// Setup gesture handlers for swipe up to expand
	const { gestureState, bindVerticalDrag } = usePlayerGestures({
		onExpand,
		isFullscreen: false,
	});

	// Handle play/pause toggle using store actions
	const handlePlayPause = (e: React.MouseEvent) => {
		e.stopPropagation();
		getPlayerActions().togglePlay();
	};

	// Handle 30-second rewind
	const handleRewind = (e: React.MouseEvent) => {
		e.stopPropagation();
		getPlayerActions().seekRelative(-30);
	};

	// Get artwork URL or fallback
	const artworkUrl = currentMedia?.artwork || "/podhive-orange.png";

	// Display text: prefer current chapter, fallback to media title
	const displayTitle =
		currentChapter?.text || currentMedia?.title || "No media";
	const displaySubtitle = currentChapter
		? currentMedia?.subtitle
		: `Chapter ${activeChapterIndex + 1}/${chapters.length}`;

	return (
		// biome-ignore lint/a11y/useSemanticElements: Using div to avoid nested button issue (play/pause button inside)
		<div
			className="fixed bottom-[calc(74px+env(safe-area-inset-bottom))] left-3 right-3 z-50 rounded-[26px] liquid-glass cursor-pointer text-left touch-none overflow-hidden"
			onClick={onExpand}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onExpand();
				}
			}}
			role="button"
			tabIndex={0}
			aria-label="Expand player - Swipe up to open"
			{...bindVerticalDrag()}
			style={{
				transform: gestureState.isDragging
					? `translateY(${gestureState.y}px)`
					: undefined,
				opacity: gestureState.isDragging ? gestureState.opacity : 1,
				transition: gestureState.isDragging
					? "none"
					: "transform 0.2s ease-out, opacity 0.2s ease-out",
			}}
		>
			{/* Subtle progress line at the top of the pill */}
			<div className="absolute top-0 left-0 right-0 h-[2px] bg-foreground/[0.06]">
				<div
					className="h-full bg-foreground/60 transition-all duration-300 ease-linear"
					style={{ width: `${progress}%` }}
				/>
			</div>

			{/* Player bar content */}
			<div className="flex items-center gap-1.5 px-2.5 py-1">
				{/* Artwork thumbnail */}
				<div className="flex-shrink-0 w-8 h-8 rounded-md overflow-hidden shadow-lg">
					<img
						src={artworkUrl}
						alt={currentMedia?.title || "Podcast artwork"}
						className="w-full h-full object-cover"
					/>
				</div>

				{/* Metadata */}
				<div className="flex-1 min-w-0">
					<p className="text-xs font-semibold text-foreground truncate leading-tight">
						{displayTitle}
					</p>
					<p className="text-[10px] text-muted-foreground truncate leading-tight mt-px">
						{displaySubtitle}
					</p>
				</div>

				{/* 30-second rewind button */}
				<button
					onClick={handleRewind}
					className="h-8 w-8 flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform duration-150 relative"
					type="button"
					aria-label="Rewind 30 seconds"
				>
					<RotateCcw
						className="h-[18px] w-[18px] text-muted-foreground"
						strokeWidth={1.8}
					/>
					<span className="absolute inset-0 flex items-center justify-center text-[6px] font-bold text-muted-foreground">
						30
					</span>
				</button>

				{/* Play/Pause button */}
				<button
					onClick={handlePlayPause}
					className="h-8 w-8 flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform duration-150"
					type="button"
					aria-label={isPaused ? "Play" : "Pause"}
				>
					{isPaused ? (
						<Play className="h-[18px] w-[18px] text-foreground fill-foreground" />
					) : (
						<Pause className="h-3.5 w-3.5 text-foreground fill-foreground" />
					)}
				</button>
			</div>
		</div>
	);
}
